import { z } from "zod";
import { FirebaseId } from "./primitives";

export const PageTitleKey = z.enum([
    "snippets.title.new_message",
    "snippets.title.new_messages",
    "snippets.title.waiting_for_feedback",
    "snippets.title.working_on_request",
    "snippets.title.working_on_requests",
    "snippets.title.how_can_we_help",
]);
export type PageTitleKey = z.infer<typeof PageTitleKey>;

export const SnippetState = z.enum([
    "unread",
    "awaiting_reply",
    "replied",
    "new_request",
    "in_progress",
    "no_active",
]);
export type SnippetState = z.infer<typeof SnippetState>;

export const SectionKey = z.enum(["please_review", "in_progress", "none"]);
export type SectionKey = z.infer<typeof SectionKey>;

export const GeneralChatState = z.enum(["unread", "read"]);
export type GeneralChatState = z.infer<typeof GeneralChatState>;

export const MediaType = z.enum(["image", "document", "audio", "proposal", "booking"]);
export type MediaType = z.infer<typeof MediaType>;

export const MessagePlaceholderKey = z.enum([
    "message.preview.shared_image",
    "message.preview.shared_document",
    "message.preview.shared_audio",
    "message.preview.shared_proposal",
    "message.preview.shared_booking",
]);
export type MessagePlaceholderKey = z.infer<typeof MessagePlaceholderKey>;

export const MessagePreviewDto = z.object({
    senderId: z.string().nullable(),
    senderName: z.string(),
    senderAvatarUrl: z.url().nullable(),
    sentByMember: z.boolean(),
    text: z.string().nullable(),
    placeholderKey: MessagePlaceholderKey.nullable(),
    mediaType: MediaType.nullable(),
    mediaImageUrls: z.array(z.url()).max(2),
    isConfirmedBooking: z.boolean(),
});
export type MessagePreviewDto = z.infer<typeof MessagePreviewDto>;

export const GeneralChatSnippetDto = z.object({
    type: z.literal("general_chat"),
    state: GeneralChatState,
    unreadCount: z.int().min(0),
    message: MessagePreviewDto.nullable(),
    sectionKey: SectionKey,
});
export type GeneralChatSnippetDto = z.infer<typeof GeneralChatSnippetDto>;

export const RequestSnippetDto = z.object({
    type: z.literal("request"),
    id: FirebaseId,
    title: z.string().nullable(),
    startDate: z.string().nullable(),
    startTime: z.string().nullable(),
    endDate: z.string().nullable(),
    endTime: z.string().nullable(),
    guestCount: z.int().nullable(),
    state: SnippetState,
    status: z.string(),
    unreadCount: z.int().min(0),
    assignedToId: z.string().nullable(),
    assignedToName: z.string().nullable(),
    message: MessagePreviewDto.nullable(),
    nextUpdateAt: z.string().nullable(),
    sectionKey: SectionKey,
});
export type RequestSnippetDto = z.infer<typeof RequestSnippetDto>;

export const SnippetDto = z.discriminatedUnion("type", [
    GeneralChatSnippetDto,
    RequestSnippetDto,
]);
export type SnippetDto = z.infer<typeof SnippetDto>;

export const SnippetsResponseDto = z.object({
    pageTitle: PageTitleKey,
    totalUnreadCount: z.int().min(0),
    defaultVisibleCount: z.int().min(0),
    snippets: z.array(SnippetDto),
});
export type SnippetsResponseDto = z.infer<typeof SnippetsResponseDto>;
